import { FC, useEffect, useMemo, useState } from "react";
import { ITrialCalendarDay, ITrialCalendarFilter, trialLessonService } from "../../../../entities/trialLesson";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { useMyActions } from "../../../../entities/my";
import { MyError } from "../../../../shared/lib/error/MyError";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { useGetMonthWeeks } from "../../../../shared/lib/hooks/useGetMonthWeeks";
import classes from './trialCalendarMonth.module.scss'
import { LoaderSpinner } from "../../../../shared/ui/spinner";

interface IProps {
    filter: ITrialCalendarFilter;
    creationFilter?: ITrialCalendarFilter;
    onSelectDate?: (date: Date) => void;
}

export const TrialCalendarMonth: FC<IProps> = ({filter, creationFilter, onSelectDate}) => {

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()

    const [visibleMonth, setVisibleMonth] = useState<Date>(new Date())
    const [calendarDays, setCalendarDays] = useState<ITrialCalendarDay[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const weeks = useGetMonthWeeks(visibleMonth)
    const monthTitle = visibleMonth
        .toLocaleString('ru-RU', {month: 'long', year: 'numeric'})
        .replace(/\s*г\.$/, '')

    const calendarDaysMap = useMemo(() => {
        return new Map(
            calendarDays.map(day => [
                `${day.date.year}-${day.date.month}-${day.date.day}`,
                day,
            ])
        )
    }, [calendarDays])

    const getCalendarDayInfo = (date: Date) => {
        return calendarDaysMap.get(
            `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
        )
    }

    const getListInfo = (date: Date) => {
        const calendarDay = getCalendarDayInfo(date)

        if(!calendarDay) return null

        const items = [
            {label: 'Назначенные', count: calendarDay.assignedCount, className: classes.assigned},
            {label: 'Проведённые', count: calendarDay.completedCount, className: classes.completed},
            {label: 'Слиты', count: calendarDay.lostCount, className: classes.lost},
            {label: 'Проведённые', count: calendarDay.orangeCount, className: classes.orange},
            {label: 'Студент создан', count: calendarDay.studentCreatedCount, className: classes.studentCreated},
            {label: 'Закрыто по пост графику', count: calendarDay.regularScheduleHours ?? '0', className: classes.regularSchedule},
        ].filter(item => Number(item.count) > 0)

        if(!items.length) return null

        return (
            <ul className={classes.infoList}>
                {items.map(item => (
                    <li className={`${classes.item} ${item.className}`} key={item.label}>
                        <span className={classes.infoLabel}>{item.label}</span>
                        <span className={classes.infoCount}>{item.count}</span>
                    </li>
                ))}
            </ul>
        )
    }

    const getDayMarkers = (date: Date) => {
        const calendarDay = getCalendarDayInfo(date)

        if(!calendarDay) return null

        return (
            <>
                {calendarDay.assignedCount > 0 && (
                    <span className={`${classes.marker} ${classes.assignedMarker}`} aria-hidden="true" />
                )}
                {calendarDay.lostCount > 0 && (
                    <span className={`${classes.marker} ${classes.lostMarker}`} aria-hidden="true" />
                )}
                {calendarDay.orangeCount > 0 && (
                    <span className={`${classes.marker} ${classes.orangeMarker}`} aria-hidden="true" />
                )}
                {calendarDay.completedCount > 0 && (
                    <span className={`${classes.marker} ${classes.completedMarker}`} aria-hidden="true" />
                )}
                {calendarDay.studentCreatedCount > 0 && (
                    <span className={`${classes.marker} ${classes.studentCreatedMarker}`} aria-hidden="true" />
                )}
            </>
        )
    }

    const isPastDate = (date: Date): boolean => {
        const today = new Date()
        const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate())
        return date.getTime() < todayStart.getTime()
    }

    const getAvailabilityTitle = (date: Date, calendarDay?: ITrialCalendarDay): string => {
        if(isPastDate(date)) return 'Прошедший день'

        let availability = ''
        switch(calendarDay?.availabilityStatus){
            case 'TRIAL_AVAILABILITY_STATUS_AVAILABLE':
                availability = `Доступно свободных слотов: ${calendarDay.availableSlotsCount}`
                break
            case 'TRIAL_AVAILABILITY_STATUS_UNAVAILABLE':
                availability = 'Нет непрерывного свободного интервала требуемой длительности'
                break
            case 'TRIAL_AVAILABILITY_STATUS_NOT_CALCULATED':
                availability = 'Выберите один Telegram, один тег, пол и одного исполнителя'
                break
            default:
                availability = ''
        }

        return availability
    }

    useEffect(() => {
        let isActual = true
        setCalendarDays([])

        const getData = async() => {
            try{
                setIsLoading(true)
                const response = await trialLessonService.getTrialMonthCalendar({
                    year: visibleMonth.getFullYear(),
                    month: visibleMonth.getMonth() + 1,
                    filter,
                    creationFilter,
                })

                if(isActual) setCalendarDays(response.days)
            }
            catch(e){
                console.log(e)
                if(e instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else if(e instanceof MyError){
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else{
                    setGlobalMessage({message: 'Ошибка при получении календаря пробных занятий', type: 'error'})
                }
            }
            finally{
                setIsLoading(false)
            }
        }

        getData()

        return () => {
            isActual = false
        }
    }, [visibleMonth, filter, creationFilter, setGlobalMessage, setIsAuth])

    return (
        isLoading
            ?
        <section className={classes.loader}>
            <LoaderSpinner />
        </section>
            :
        <section className={classes.container}>
            <section className={classes.header}>
                <h2 className={classes.title}>{monthTitle}</h2>
                <nav className={classes.navigation} aria-label="Переключение месяца">
                    <button
                        type="button"
                        className={classes.navigationButton}
                        onClick={() => {
                            setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1))
                        }}
                        aria-label="Предыдущий месяц"
                    >
                        ‹
                    </button>
                    <button
                        type="button"
                        className={classes.navigationButton}
                        onClick={() => {
                            setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1))
                        }}
                        aria-label="Следующий месяц"
                    >
                        ›
                    </button>
                </nav>
            </section>

            <section className={classes.calendarFrame}>
                <table className={classes.month}>
                    <thead>
                        <tr>
                            <th scope="col">Пн</th>
                            <th scope="col">Вт</th>
                            <th scope="col">Ср</th>
                            <th scope="col">Чт</th>
                            <th scope="col">Пт</th>
                            <th scope="col">Сб</th>
                            <th scope="col">Вс</th>
                        </tr>
                    </thead>
                    <tbody>
                        {weeks.map((week, weekIndex) => (
                            <tr key={weekIndex}>
                                {week.map(day => {
                                    const calendarDay = getCalendarDayInfo(day.date)
                                    const isPast = day.class !== 'opacity' && isPastDate(day.date)
                                    const isAvailable = !isPast && calendarDay?.availabilityStatus ===
                                        'TRIAL_AVAILABILITY_STATUS_AVAILABLE'
                                    const isUnavailable = !isPast && calendarDay?.availabilityStatus ===
                                        'TRIAL_AVAILABILITY_STATUS_UNAVAILABLE'

                                    return <td
                                        key={day.date.getTime()}
                                        tabIndex={day.class === 'opacity' ? -1 : 0}
                                        title={day.class === 'opacity' ? undefined : getAvailabilityTitle(day.date, calendarDay)}
                                        onClick={day.class === 'opacity' ? undefined : () => onSelectDate?.(day.date)}
                                        onKeyDown={event => {
                                            if(day.class === 'opacity') return
                                            if(event.key === 'Enter' || event.key === ' '){
                                                event.preventDefault()
                                                onSelectDate?.(day.date)
                                            }
                                        }}
                                        className={classes.day +
                                            (day.class === 'current' ? ` ${classes.current}` : '') +
                                            (day.class === 'opacity' ? ` ${classes.opacity}` : '') +
                                            (isPast ? ` ${classes.pastDay}` : '') +
                                            (isAvailable ? ` ${classes.availableDay}` : '') +
                                            (isUnavailable ? ` ${classes.unavailableDay}` : '')
                                        }
                                    >
                                        {day.class !== 'opacity' && (
                                            <>
                                                <span className={classes.dayNumber}>{day.date.getDate()}</span>
                                                {isAvailable && (
                                                    <span className={classes.availabilityBadge}>
                                                        {calendarDay?.availableSlotsCount} сл.
                                                    </span>
                                                )}
                                                {getDayMarkers(day.date)}
                                                {getListInfo(day.date)}
                                            </>
                                        )}
                                    </td>
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>
        </section>
    )
}
